import React, { useState, useEffect } from 'react';
import { LogOut, AlertTriangle, CheckCircle, Home, Loader, Plus, Trash2 } from 'lucide-react';
import { useAppTheme } from '../../contexts/ThemeContext';
import { getMoveOutPreview, submitMoveOut } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// MoveOut.js
// Props:
//   tenant    — current tenant object
//   onClose   — close this dialog
//   onMoveOut — called after successful move-out so parent can refresh/close
//
// Parent dialog must give this component a tall/wide fixed container.
// This component owns its own scroll internally.
// ─────────────────────────────────────────────────────────────────────────────

// ── Hoisted helper — must live outside the component so it's never in the
//    temporal dead zone when called from computed values during render. ────────
const fmt = (n) => Number(n ?? 0).toLocaleString();


// ── Shell is hoisted outside MoveOut so its identity stays stable across renders.
//    If defined inside, every state update creates a new component type → full
//    remount → scroll resets to top on every keystroke / radio change. ──────────
const Shell = ({ theme: { bdr, bg, txt, ERR }, tenant, children, footer }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: 860, overflow: 'hidden' }}>

    {/* Fixed header */}
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12,
      padding: '20px 28px', borderBottom: `1px solid ${bdr}`, backgroundColor: bg.primary,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: `${ERR}18`,
      }}>
        <LogOut size={20} style={{ color: ERR }} />
      </div>
      <div>
        <p style={{ fontWeight: 700, fontSize: 17, color: txt.primary, margin: 0, lineHeight: 1.2 }}>
          Move Out — {tenant.name}
        </p>
        <p style={{ fontSize: 13, color: txt.secondary, margin: '3px 0 0' }}>
          Unit {tenant.houseNumber} · {tenant.propertyName}
        </p>
      </div>
    </div>

    {/* Scrollable body */}
    <div style={{
      flex: 1, overflowY: 'auto', padding: '24px 28px',
      display: 'flex', flexDirection: 'column', gap: 22,
    }}>
      {children}
    </div>

    {/* Fixed footer */}
    <div style={{
      flexShrink: 0, display: 'flex', gap: 10,
      padding: '16px 28px', borderTop: `1px solid ${bdr}`, backgroundColor: bg.primary,
    }}>
      {footer}
    </div>
  </div>
);

const MoveOut = ({ tenant, onClose, onMoveOut }) => {
  const { palette, Colors } = useAppTheme();
  const bg   = palette.background;
  const txt  = palette.text;
  const bdr  = palette.border;
  const P    = Colors.primary.main;
  const ERR  = Colors.status.error;
  const OK   = Colors.status.success;
  const WARN = Colors.status?.warning ?? '#F59E0B';

  // ── state ──────────────────────────────────────────────────────────────────
  const [step, setStep]                     = useState('form');
  const [preview, setPreview]               = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState(null);
  const [result, setResult]                 = useState(null);

  // form fields
  const movedOutDate = new Date().toISOString().split('T')[0];
  const [depositAction, setDepositAction]         = useState('offset_then_return');
  const [unitReleaseStatus, setUnitReleaseStatus] = useState('');  // empty = not yet chosen
  const [notes, setNotes]                         = useState('');
  const [deductions, setDeductions]               = useState([]);  // [{ reason, amount }]

  const inputStyle = {
    backgroundColor: bg.tertiary,
    border: `1px solid ${bdr}`,
    color: txt.primary,
  };

  // ── load preview ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingPreview(true);
        const data = await getMoveOutPreview(tenant.id);
        setPreview(data);
        if (!data.hasArrears)         setDepositAction('return_full');
        else                          setDepositAction('offset_then_return');
      } catch {
        setError('Failed to load move-out preview. Please try again.');
      } finally {
        setLoadingPreview(false);
      }
    };
    load();
  }, [tenant.id]);

  // ── deduction helpers ──────────────────────────────────────────────────────
  const totalDeductions = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const addDeduction    = () => setDeductions(p => [...p, { reason: '', amount: '' }]);
  const removeDeduction = (i) => setDeductions(p => p.filter((_, idx) => idx !== i));
  const updateDeduction = (i, field, val) =>
    setDeductions(p => p.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  // ── live refund calc ───────────────────────────────────────────────────────
  const refundCalc = (() => {
    if (!preview || depositAction === 'forfeit') return null;
    const held         = preview.depositHeld ?? 0;
    const offsetNeeded = preview.offsetNeeded ?? 0;

    if (depositAction === 'return_full') {
      const deducted = Math.min(totalDeductions, held);
      return { held, offsetUsed: 0, deducted, refund: Math.max(held - deducted, 0) };
    }
    if (depositAction === 'offset_then_return') {
      const offsetUsed   = Math.min(offsetNeeded, held);
      const afterArrears = Math.max(held - offsetUsed, 0);
      const deducted     = Math.min(totalDeductions, afterArrears);
      return { held, offsetUsed, deducted, refund: Math.max(afterArrears - deducted, 0) };
    }
    if (depositAction === 'offset_only') {
      return { held, offsetUsed: Math.min(offsetNeeded, held), deducted: 0, refund: 0 };
    }
    return null;
  })();

  // ── deposit option list ────────────────────────────────────────────────────
  const depositOptions = (() => {
    if (!preview) return [];
    const held = preview.depositHeld ?? 0;
    const opts = [];
    if (held > 0) {
      if (preview.hasArrears) {
        const actualOffset = Math.min(preview.offsetNeeded, held);
        opts.push({
          value: 'offset_then_return',
          label: 'Offset balance, return remainder',
          description: `Use KES ${fmt(actualOffset)} to clear arrears${actualOffset < preview.offsetNeeded ? ` (partial — KES ${fmt(preview.offsetNeeded - actualOffset)} remains outstanding)` : ''}, return the rest to tenant`,
        });
        // Only show "keep remainder" if there actually is a remainder after offsetting
        if (held > preview.offsetNeeded) {
          opts.push({
            value: 'offset_only',
            label: 'Offset balance only, keep remainder',
            description: `Clear arrears from deposit; KES ${fmt(held - preview.offsetNeeded)} leftover stays with company.`,
          });
        }
      }
      opts.push({
        value: 'return_full',
        label: 'Return full deposit',
        description: `Return KES ${fmt(held)} to tenant (minus any deductions below)`,
      });
    }
    opts.push({
      value: 'forfeit',
      label: 'Forfeit deposit',
      description: 'Tenant forfeits entire deposit. No refund.',
    });
    return opts;
  })();

  // confirm only enabled once unit release is picked
  const canSubmit = unitReleaseStatus !== '' && !submitting;

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    for (const d of deductions) {
      if (!d.reason.trim() || !d.amount || parseFloat(d.amount) <= 0) {
        setError('Each additional deduction needs a reason and a positive amount.');
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitMoveOut(tenant.id, {
        movedOutDate,
        depositAction,
        unitReleaseStatus,
        notes,
        additionalDeductions: depositAction !== 'forfeit'
          ? deductions.map(d => ({ reason: d.reason.trim(), amount: parseFloat(d.amount) }))
          : [],
      });
      setResult(res);
      setStep('done');
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Move-out failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  // ── STEP: done ─────────────────────────────────────────────────────────────
  if (step === 'done' && result) {
    return (
      <Shell theme={{ bdr, bg, txt, ERR }} tenant={tenant} footer={
        <button onClick={() => onMoveOut(result)} style={{
          flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
          backgroundColor: P, color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
        }}>
          Done
        </button>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, padding: '20px 0' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: `${OK}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={32} style={{ color: OK }} />
          </div>
          <p style={{ fontWeight: 700, fontSize: 20, color: txt.primary, margin: 0 }}>Move-Out Complete</p>
          <p style={{ fontSize: 13, color: txt.secondary, margin: 0 }}>
            {result.tenantName} has been moved out of unit {result.unitNumber}.
          </p>
        </div>

        <div style={{ borderRadius: 12, padding: '16px 20px', backgroundColor: bg.tertiary, border: `1px solid ${bdr}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            ['Move-out date',          result.movedOutAt],
            ['Deposit used to offset', `KES ${fmt(result.depositUsedToOffset)}`],
            ...(result.additionalDeducted > 0
              ? [['Additional deductions', `KES ${fmt(result.additionalDeducted)}`]]
              : []),
            ['Deposit returned',       `KES ${fmt(result.depositReturned)}`],
            ['Final balance',          `KES ${fmt(result.finalBalance)}`],
            ['Unit status',            result.unitReleaseStatus === 'vacant_available'
              ? 'Vacant & Available' : 'Vacant (Unavailable)'],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: txt.secondary }}>{label}</span>
              <span style={{ fontWeight: 600, color: txt.primary }}>{val}</span>
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  // ── STEP: loading ──────────────────────────────────────────────────────────
  if (loadingPreview) {
    return (
      <Shell theme={{ bdr, bg, txt, ERR }} tenant={tenant} footer={
        <button disabled style={{
          flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
          backgroundColor: ERR, color: '#fff', fontWeight: 600, fontSize: 14,
          opacity: 0.4, cursor: 'not-allowed',
        }}>
          Confirm Move-Out
        </button>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '60px 0' }}>
          <Loader size={32} className="animate-spin" style={{ color: P }} />
          <p style={{ fontSize: 13, color: txt.secondary, margin: 0 }}>Loading move-out summary…</p>
        </div>
      </Shell>
    );
  }

  // ── STEP: main form ────────────────────────────────────────────────────────
  return (
    <Shell theme={{ bdr, bg, txt, ERR }} tenant={tenant} footer={
      <>
        <button onClick={onClose} disabled={submitting} style={{
          flex: 1, padding: '12px 0', borderRadius: 12, fontSize: 14, fontWeight: 500,
          backgroundColor: bg.tertiary, border: `1px solid ${bdr}`, color: txt.primary, cursor: 'pointer',
        }}>
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={!unitReleaseStatus ? 'Select how to release the unit first' : ''}
          style={{
            flex: 1, padding: '12px 0', borderRadius: 12, fontSize: 14, fontWeight: 600,
            backgroundColor: ERR, color: '#fff', border: 'none',
            opacity: canSubmit ? 1 : 0.38,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'opacity 0.2s',
          }}>
          {submitting
            ? <><Loader size={16} className="animate-spin" /> Processing…</>
            : <><LogOut size={16} /> Confirm Move-Out</>}
        </button>
      </>
    }>

      {/* Error */}
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13, backgroundColor: `${ERR}15`, border: `1px solid ${ERR}40`, color: ERR }}>
          {error}
        </div>
      )}

      {/* ── Financial summary ── */}
      {preview && (
        <div style={{ borderRadius: 14, padding: '16px 20px', backgroundColor: bg.tertiary, border: `1px solid ${bdr}` }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: txt.secondary, margin: '0 0 14px' }}>
            Financial Summary
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
            {[
              { label: 'Current Balance', value: `KES ${fmt(preview.currentBalance)}`, color: preview.currentBalance < 0 ? ERR : OK },
              { label: 'Deposit Held',    value: `KES ${fmt(preview.depositHeld)}`,    color: txt.primary },
              ...(preview.hasArrears ? [
                { label: 'Arrears',      value: `KES ${fmt(preview.offsetNeeded)}`,       color: ERR },
                { label: 'After Offset', value: `KES ${fmt(preview.depositAfterOffset)}`, color: OK  },
              ] : []),
            ].map(({ label, value, color }) => (
              <div key={label} style={{ borderRadius: 10, padding: '10px 14px', backgroundColor: bg.secondary }}>
                <p style={{ fontSize: 11, color: txt.secondary, margin: '0 0 4px' }}>{label}</p>
                <p style={{ fontWeight: 700, fontSize: 13, color, margin: 0 }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Arrears — deposit can't cover */}
          {preview.hasArrears && !preview.canFullyOffset && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, backgroundColor: `${ERR}10`, border: `1px solid ${ERR}30` }}>
              <AlertTriangle size={15} style={{ color: ERR, flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: ERR, margin: 0 }}>
                Deposit is insufficient to fully clear the balance. KES {fmt(Math.abs(preview.remainingBalanceAfterOffset))} will remain outstanding.
              </p>
            </div>
          )}

          {/* Arrears — deposit covers + surplus */}
          {preview.hasArrears && preview.canFullyOffset && preview.depositAfterOffset > 0 && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, backgroundColor: `${OK}10`, border: `1px solid ${OK}30` }}>
              <CheckCircle size={15} style={{ color: OK, flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: OK, margin: 0 }}>
                Deposit fully covers arrears. KES {fmt(preview.depositAfterOffset)} remains — choose below what to do with it.
              </p>
            </div>
          )}

          {/* No arrears — full deposit available */}
          {!preview.hasArrears && (preview.depositHeld ?? 0) > 0 && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, backgroundColor: `${OK}10`, border: `1px solid ${OK}30` }}>
              <CheckCircle size={15} style={{ color: OK, flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: OK, margin: 0 }}>
                No outstanding balance. Full deposit of KES {fmt(preview.depositHeld)} is available to return.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Deposit action ── */}
      <div>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: txt.secondary, marginBottom: 10 }}>
          Security Deposit Action
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {depositOptions.map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
              backgroundColor: depositAction === opt.value ? `${P}15` : bg.tertiary,
              border: `1px solid ${depositAction === opt.value ? P : bdr}`,
              transition: 'all 0.15s',
            }}>
              <input type="radio" name="depositAction" value={opt.value}
                checked={depositAction === opt.value}
                onChange={() => setDepositAction(opt.value)}
                style={{ marginTop: 3, flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: txt.primary, margin: 0 }}>{opt.label}</p>
                <p style={{ fontSize: 12, color: txt.secondary, margin: '3px 0 0' }}>{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* ── Additional deductions ── */}
      {depositAction !== 'forfeit' && (preview?.depositHeld ?? 0) > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: txt.secondary, margin: 0 }}>
                Additional Deductions from Deposit
              </p>
              <p style={{ fontSize: 11, color: txt.muted, margin: '3px 0 0' }}>
                e.g. damages, cleaning — deducted before refund
              </p>
            </div>
            <button type="button" onClick={addDeduction} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              backgroundColor: `${P}15`, color: P, border: `1px solid ${P}40`,
              cursor: 'pointer', flexShrink: 0, marginLeft: 12,
            }}>
              <Plus size={13} /> Add charge
            </button>
          </div>

          {deductions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {deductions.map((d, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 12px', borderRadius: 12,
                  backgroundColor: bg.tertiary, border: `1px solid ${bdr}`,
                }}>
                  <input
                    type="text"
                    placeholder="Reason (e.g. broken window)"
                    value={d.reason}
                    onChange={e => updateDeduction(i, 'reason', e.target.value)}
                    style={{
                      flex: 1, padding: '7px 12px', borderRadius: 8, fontSize: 13, outline: 'none',
                      backgroundColor: bg.secondary, border: `1px solid ${bdr}`, color: txt.primary,
                    }}
                  />
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px',
                    borderRadius: 8, backgroundColor: bg.secondary, border: `1px solid ${bdr}`,
                  }}>
                    <span style={{ fontSize: 11, color: txt.muted }}>KES</span>
                    <input
                      type="number" min="0" placeholder="0"
                      value={d.amount}
                      onChange={e => updateDeduction(i, 'amount', e.target.value)}
                      style={{ width: 90, padding: '7px 4px', fontSize: 13, background: 'transparent', border: 'none', outline: 'none', color: txt.primary }}
                    />
                  </div>
                  <button type="button" onClick={() => removeDeduction(i)} style={{
                    padding: 7, borderRadius: 8, color: ERR,
                    backgroundColor: `${ERR}10`, border: 'none', cursor: 'pointer', display: 'flex',
                  }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <p style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: ERR, margin: '4px 0 0' }}>
                Total additional deductions: KES {fmt(totalDeductions)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Live refund breakdown ── */}
      {depositAction !== 'forfeit' && refundCalc && (preview?.depositHeld ?? 0) > 0 && (
        <div style={{
          borderRadius: 12, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10,
          backgroundColor: depositAction === 'offset_only' ? `${WARN}0A` : `${OK}08`,
          border: `1px solid ${depositAction === 'offset_only' ? WARN + '40' : OK + '35'}`,
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: depositAction === 'offset_only' ? WARN : OK, margin: 0 }}>
            Deposit Breakdown
          </p>
          {[
            { label: 'Deposit held',         value: refundCalc.held,      signed: false },
            ...(refundCalc.offsetUsed > 0 ? [{ label: 'Used to clear arrears',  value: refundCalc.offsetUsed, signed: true }] : []),
            ...(refundCalc.deducted    > 0 ? [{ label: 'Additional deductions', value: refundCalc.deducted,   signed: true }] : []),
          ].map(({ label, value, signed }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: txt.secondary }}>{label}</span>
              <span style={{ fontWeight: 600, color: signed ? ERR : txt.primary }}>
                {signed ? `− KES ${fmt(value)}` : `KES ${fmt(value)}`}
              </span>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${depositAction === 'offset_only' ? WARN + '40' : OK + '35'}` }} />
          {depositAction === 'offset_only' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
              <span style={{ color: WARN }}>Tenant receives</span>
              <span style={{ color: WARN }}>KES 0</span>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
              <span style={{ color: OK }}>Tenant receives</span>
              <span style={{ color: OK }}>KES {fmt(refundCalc.refund)}</span>
            </div>
          )}
          {totalDeductions > (refundCalc.held - refundCalc.offsetUsed) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: WARN }}>
              <AlertTriangle size={14} style={{ flexShrink: 0 }} />
              Deductions exceed available deposit — refund capped at KES 0.
            </div>
          )}
        </div>
      )}

      {/* ── Release unit ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: txt.secondary, margin: 0 }}>
            Release Unit As
          </label>
          {!unitReleaseStatus && (
            <span style={{ fontSize: 11, color: WARN, fontWeight: 500 }}>
              ← select to enable confirm button
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { value: 'vacant_available',   label: 'Vacant & Available',   Icon: CheckCircle, desc: 'Ready for new bookings' },
            { value: 'vacant_unavailable', label: 'Vacant (Unavailable)', Icon: Home,        desc: 'Under maintenance or hold' },
          ].map(({ value, label, Icon, desc }) => (
            <button key={value} type="button"
              onClick={() => setUnitReleaseStatus(value)}
              style={{
                padding: '14px 16px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                backgroundColor: unitReleaseStatus === value ? `${P}15` : bg.tertiary,
                border: `2px solid ${unitReleaseStatus === value ? P : bdr}`,
                transition: 'all 0.15s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: unitReleaseStatus === value ? P : txt.secondary }}>
                <Icon size={15} />
                <span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span>
              </div>
              <p style={{ fontSize: 11, color: txt.muted, margin: 0 }}>{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── Notes ── */}
      <div>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: txt.secondary, marginBottom: 8 }}>
          Notes (optional)
        </label>
        <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Reason for move-out, any remarks…"
          style={{ ...inputStyle, width: '100%', padding: '9px 14px', borderRadius: 10, fontSize: 13, resize: 'none', boxSizing: 'border-box', outline: 'none' }} />
      </div>

    </Shell>
  );
};

export default MoveOut;